// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/components/v2/history/MidasHistoryRowV2.tsx
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes
// Phase: 4 — sub-phase 4.4a (row layout, no data wiring)
//
// Hairline conversation row per designer artboard `R5-0`. The row is a flat
// 4-column grid sitting directly on the page bone-deep surface — no card,
// no border around the row, no shadow. Only a 1px hairline at the bottom.
//
// Columns:
//   1. Time (64px fixed)         — JBM 12 weight 500 graphite + JBM 10 muted sub-line
//   2. Title + snippet (flex-1)  — Sora 15 weight 600 graphite + Sora 13 warm-stone
//   3. Machine pill (auto)       — pill with status dot + JBM 10 weight 500 mono name
//   4. Trailing arrow (16px)     — light stone stroke, warms to terracotta on hover
//
// Sub-phase scope (4.4a — THIS COMMIT):
//   • Drop the previous card shell (border / radius / shadow / min-h)
//   • Build the 4-column hairline grid
//   • Move machine label from above-title to right-side pill (with status dot)
//   • Restyle title typography (16/22 weight 500 → 15/18 weight 600)
//   • Bump page max-width and add active-row left-bar indicator (page-level)
//
// Sub-phase 4.4b (DEFERRED — gated on bot-side DTO additions):
//   • Wire `last_message_preview` → snippet under title (currently omitted)
//   • Wire `message_count` → "{N} msgs" in time sub-line (currently empty)
//   • The matching markup placeholders below carry TODO 4.4b markers so
//     the data wiring lands without re-layout work.
//
// Click → onOpen(threadId). Caller is responsible for navigation.
import React from "react";

import { Thread } from "../../../midasPageModels";

interface MidasHistoryRowV2Props {
  thread: Thread;
  /** Resolved machine display name ("Mazak QT-200") or null for platform threads. */
  machineLabel: string | null;
  /** Currently-loaded thread id (or null) — drives the left-bar active indicator. */
  activeThreadId: string | null;
  /** Click handler — opens the thread. */
  onOpen: (threadId: string) => void;
  /** Translation function — keeps the row stateless and i18n-safe. */
  t: (key: string, fallback: string) => string;
}

/** Format the time column — "HH:MM" + DD.MM date subtitle (4.4b will replace the sub-line). */
const formatTimeCol = (iso: string): { time: string; sub: string } => {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const time = `${hh}:${mm}`;

  // 4.4a stub: sub-line falls back to short DD.MM until the bot DTO ships
  // `message_count`. Designer JSX shows "{N} msgs" in this slot — that markup
  // will land in 4.4b. Keeping a non-empty fallback here means the row's
  // visual height matches the designer artboard right now, and 4.4b only
  // swaps the inner text without touching layout.
  const dd = String(d.getDate()).padStart(2, "0");
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const sub = `${dd}.${mo}`;

  return { time, sub };
};

const MidasHistoryRowV2: React.FC<MidasHistoryRowV2Props> = ({
  thread,
  machineLabel,
  activeThreadId,
  onOpen,
  t,
}) => {
  const { time, sub } = formatTimeCol(
    thread.last_message_at || thread.created_at
  );

  const isActive = activeThreadId !== null && activeThreadId === thread.id;
  const isPlatform = !thread.machine_id;
  // Status-dot color: terracotta for Platform Mode threads, green for any
  // machine-context thread. Per designer `R5-0`.
  const dotColor = isPlatform ? "#D4572B" : "#3FA26B";
  const pillLabel =
    machineLabel ||
    t("midas.historyPage.platformLabel", "PLATFORM").toUpperCase();

  const ariaLabel = `${
    machineLabel || t("midas.historyPage.platformMode", "Platform Mode")
  } — ${thread.title} — ${time} ${sub}`;

  return (
    <li className="relative">
      {/* Active-row left-bar indicator (3px terracotta). Lives outside the
          button so it overlays the row's hairline edge cleanly without
          fighting button padding. Conditional render keeps inactive rows
          paint-clean. */}
      {isActive && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-[3px] rounded-r-sm bg-midas-terracotta"
        />
      )}
      <button
        type="button"
        onClick={() => onOpen(thread.id)}
        aria-label={ariaLabel}
        aria-current={isActive ? "true" : undefined}
        title={thread.title}
        // Hairline row: NO card border / radius / shadow / min-h-76. Just a
        // 1px bottom hairline + py 14 / px 12 + 16px gap, sitting flat on the
        // page bone-deep surface. Hover bumps the row bg to a slightly
        // brighter bone tone (#FCFAF4) and warms the trailing arrow stroke
        // to terracotta — that's the entire hover treatment, no border /
        // shadow / scale.
        className="group flex w-full items-start gap-4 border-b border-solid border-[#EBE5D9] bg-transparent px-3 py-3.5 text-left outline-none transition-colors duration-150 hover:bg-[#FCFAF4] focus-visible:bg-[#FCFAF4] focus-visible:ring-2 focus-visible:ring-midas-terracotta focus-visible:ring-offset-1 focus-visible:ring-offset-midas-bone-deep motion-reduce:transition-none"
      >
        {/* ── Column 1: Time + sub-line (64px fixed) ──
            Top-aligned with a 2px nudge so the time baseline sits with
            the title's optical baseline, per the designer JSX (`pt-0.5`). */}
        <div className="flex w-16 flex-shrink-0 flex-col items-start pt-0.5">
          <span
            className="font-mono text-[12px] font-medium leading-4 tracking-[0.02em] text-[#1A1614]"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            {time}
          </span>
          {/* 4.4b STUB: this sub-line currently shows DD.MM. Once the bot
              DTO adds `message_count`, replace `sub` with the formatted
              "{count} msgs" string. The element MUST stay so 4.4b can wire
              data without re-layout. */}
          <span
            className="font-mono text-[10px] leading-3 tracking-[0.04em] text-[#9A9186]"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            {sub}
          </span>
        </div>

        {/* ── Column 2: Title + snippet (flex-1, min-w-0) ──
            Snippet element renders empty until 4.4b lands. We render it
            unconditionally with a 1-line min-height so the row's visual
            height stays consistent before/after 4.4b ships. The empty
            paragraph carries no visible text and zero padding — only its
            min-height contributes to layout. */}
        <div className="flex min-w-0 flex-grow flex-shrink basis-0 flex-col gap-1">
          <span
            className="block w-full overflow-hidden text-ellipsis whitespace-nowrap font-sora text-[15px] font-semibold leading-[18px] tracking-[-0.005em] text-[#1A1614]"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            {thread.title}
          </span>
          {/* 4.4b STUB: snippet preview line. When the bot DTO adds
              `last_message_preview`, render `thread.last_message_preview`
              here truncated to one line. Until then we render an empty
              `<p>` with min-height so row height matches the designer
              artboard — 4.4b will fill in text without changing layout. */}
          <p
            aria-hidden="true"
            className="m-0 min-h-[19px] overflow-hidden text-ellipsis whitespace-nowrap font-sora text-[13px] leading-[19px] text-[#5A5147]"
            style={{ fontFamily: "'Sora', sans-serif" }}
          />
        </div>

        {/* ── Column 3: Machine pill (auto width, top-aligned) ──
            Pill carries a 6px status dot (terracotta for Platform threads,
            green for machine-context threads) + the machine name (or
            "PLATFORM" when machine_id is null). */}
        <div className="mt-0.5 flex flex-shrink-0 items-center gap-1.5 self-start rounded-full border border-solid border-[#E2DDD3] bg-[#F4F1EC] px-2.5 py-1">
          <span
            aria-hidden="true"
            className="size-1.5 flex-shrink-0 rounded-full"
            style={{ backgroundColor: dotColor }}
          />
          <span
            className="font-mono text-[10px] font-medium leading-3 tracking-[0.04em] text-[#3D362F]"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            {pillLabel}
          </span>
        </div>

        {/* ── Column 4: Trailing arrow (16px) ──
            Plain 16×16 SVG, stroke #C5BDB1, strokeWidth 1.6, marginTop 4
            so it sits visually on the title baseline. Hover warms the
            stroke to terracotta + nudges 2px right. */}
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0, marginTop: 4 }}
          className="text-[#C5BDB1] transition-all duration-150 group-hover:translate-x-0.5 group-hover:text-midas-terracotta motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
        >
          <line x1="6" y1="12" x2="18" y2="12" />
          <polyline points="13 7 18 12 13 17" />
        </svg>
      </button>
    </li>
  );
};

export default MidasHistoryRowV2;
